export const COMPANY = {
    name: "SmartLabTech",
    legalName: "SmartLabTech",
    website: "https://smartlabtech.com",
    email: "support@smartlabtech.net",
    phone: "+91 98484 44237",
    address: "Plot #74 and #75/B, Sy #735, Phase-II, S.V. Co-operative Industrial Estate, Balanagar, Hyderabad, Telangana - 500 037, India",
    jurisdiction: "India / Telangana",
    lastUpdated: "October 26, 2026",
};

export const LEGAL_LINKS = [
    { to: "/privacy-policy", label: "Privacy Policy" },
    { to: "/terms-and-conditions", label: "Terms & Conditions" },
    { to: "/cookie-policy", label: "Cookie Policy" },
];

/* ───────────────────────── PRIVACY POLICY ───────────────────────── */

export const PRIVACY_POLICY = {
    title: "Privacy Policy",
    content: `Your privacy matters to us. This policy explains what personal information {{name}} collects through our website, why we collect it, how we use and protect it, and the choices and rights you have.

## Who we are

{{legalName}} ("{{name}}", "we", "us") supplies scientific instruments, laboratory equipment, testing solutions and technical support to research laboratories, pharmaceutical companies, healthcare providers and educational institutions through [{{website}}]({{website}}).

We act as the data controller for the personal information described in this policy. Our contact details are in the "Contact us and policy changes" section below.

## Information we collect

### Information you give us

- Quote requests: your name, organisation, job title, email address, phone number, the products you are interested in and any message you write.
- Enquiries and support: the details you send us by email, phone or form, and our replies.
- Business relationship data: delivery and billing addresses, purchase orders and similar details if you become a customer.

### Information collected automatically

- Technical data such as IP address, browser type and version, device type, operating system and language.
- Usage data such as pages viewed, links clicked, time on page and the page that referred you.
- Data stored through cookies and similar technologies. See our [Cookie Policy](/cookie-policy) for details.

We do not knowingly collect sensitive personal information (such as health or biometric data) through this website. Please do not include it in your messages.

## How we use your information

- To respond to quote requests and enquiries, and to prepare quotations and proposals.
- To process orders, arrange delivery, installation and after-sales or technical support.
- To operate, secure and improve the website, including diagnosing technical problems.
- To understand how visitors use the site so we can improve our content and product information.
- To send you product, service or event updates where you have asked for them or the law allows. You can opt out at any time.
- To comply with legal obligations and to establish, exercise or defend legal claims.

## Legal bases for processing

Where data protection law such as the GDPR or UK GDPR applies, we rely on the following legal bases:

- **Contract:** to respond to your request for a quote and to perform or prepare a contract with you.
- **Legitimate interests:** to run and improve our business and website, keep it secure, and communicate with business contacts, provided this does not override your rights.
- **Consent:** for non-essential cookies and, where required, marketing emails. You can withdraw consent at any time.
- **Legal obligation:** where we must keep records or respond to lawful requests.

## Who we share information with

We do not sell your personal information. We share it only with:

- Service providers who help us run the website and business, such as hosting, email, analytics, customer-relationship and IT support providers, under written confidentiality and data protection terms.
- Manufacturers, distributors, logistics and installation partners, where needed to fulfil your quote or order.
- Professional advisers such as lawyers, auditors and insurers.
- Authorities and courts, where we are legally required to do so.
- A buyer or successor, if our business is merged, sold or restructured. We will tell you if this affects your data.

## Cookies and similar technologies

We use cookies and similar technologies to make the site work and, with your consent where required, to measure and improve it. Read our [Cookie Policy](/cookie-policy) for the types we use and how to manage them.

## How long we keep information

We keep personal information only as long as needed for the purposes above. As a general guide:

- Quote requests and enquiries: up to 24 months after our last contact, unless they lead to an order.
- Customer and order records: for the period required by tax, accounting and warranty rules, usually 6 to 10 years.
- Website analytics data: as set out in our Cookie Policy.

When data is no longer needed we delete it or anonymise it.

## Security

We use reasonable technical and organisational measures, such as encrypted connections (HTTPS), access controls and staff confidentiality obligations, to protect personal information. No online service is completely secure, so we cannot guarantee absolute security. If a breach affects your rights, we will notify you and the relevant authorities as the law requires.

## International transfers

Our providers and partners may be located outside your country. Where we transfer personal information across borders, we use appropriate safeguards, such as standard contractual clauses or adequacy decisions, where the law requires them.

## Your rights

Depending on where you live, you may have the right to:

- Access the personal information we hold about you and receive a copy.
- Correct information that is inaccurate or incomplete.
- Delete your information, in certain circumstances.
- Restrict or object to our processing, including direct marketing.
- Receive your information in a portable format.
- Withdraw consent at any time, without affecting earlier processing.
- Complain to your local data protection authority.

To exercise any of these rights, contact us using the details in the "Contact us and policy changes" section below. We may need to verify your identity first, and we will respond within the time the law requires.

## Children

Our website and services are intended for businesses and professionals. They are not directed at children under 16, and we do not knowingly collect their personal information. If you believe a child has given us personal data, please contact us and we will delete it.

## Third-party websites

The website may link to manufacturer, partner or social media sites. We are not responsible for their content or privacy practices. Please read their policies before giving them any information.

## Contact us and policy changes

We may update this policy from time to time. The "Last updated" date at the top shows when it last changed, and for significant changes we will post a notice on the website.

Questions, requests or complaints about privacy can be sent to:

**{{legalName}}** ("{{name}}")
{{address}}
Email: [{{email}}](mailto:{{email}})
Phone: {{phone}}`,
};

/* ──────────────────── TERMS AND CONDITIONS ──────────────────── */

export const TERMS_AND_CONDITIONS = {
    title: "Terms & Conditions",
    content: `Please read these terms carefully before using the {{name}} website or requesting a quote. They set out the rules for using the site and the basis on which we respond to enquiries and supply products.

## Acceptance of these terms

These Terms and Conditions ("Terms") govern your access to and use of [{{website}}]({{website}}) (the "Website"), operated by {{legalName}} ("{{name}}", "we", "us").

By using the Website or requesting a quote, you agree to these Terms and to our [Privacy Policy](/privacy-policy) and [Cookie Policy](/cookie-policy). If you use the Website on behalf of an organisation, you confirm you have authority to bind it. If you do not agree, please do not use the Website.

## About the Website and our products

The Website presents laboratory instruments, scientific equipment, testing solutions and related technical support. It is intended for business and professional users such as laboratories, healthcare providers, pharmaceutical companies and educational institutions.

We aim to keep product descriptions, images, specifications and availability accurate, but they may contain errors or change without notice. Many products are made by third parties, and the manufacturer's datasheet and documentation prevail over the Website. Images are for illustration and the delivered product may differ slightly.

## Quotes and orders

- Submitting a quote request is an enquiry only. It does not create a contract or oblige either party to buy or sell.
- A quotation is an invitation to order, is valid only for the period stated on it, and is subject to product availability.
- A contract is formed only when we confirm your purchase order in writing, or as set out in a separate written agreement signed by both parties.
- Prices exclude taxes, duties, shipping, installation and training unless the quotation says otherwise.
- We may decline or cancel an order, for example because of a pricing error, stock shortage, export restriction or failed credit check, and will refund any amount already paid for it.

Payment terms, delivery, installation, acceptance, warranty and return conditions are set out in the applicable quotation, order confirmation or supply agreement. If those documents conflict with these Terms, they prevail for that order.

## Proper use and compliance

Laboratory equipment can be hazardous if misused. You are responsible for:

- Using products only as described in the manufacturer's instructions and by suitably trained personnel.
- Complying with all applicable laws, safety standards, licensing and accreditation requirements for your laboratory and industry.
- Complying with export control, sanctions and import rules. We may refuse or delay supply where these apply.
- Not using the products for unlawful purposes or for weapons, or for any purpose prohibited by the manufacturer.

## Acceptable use of the Website

You agree not to:

- Provide false or misleading information in forms or messages.
- Attempt to gain unauthorised access to the Website, its servers or any connected system.
- Introduce viruses, malware or code intended to disrupt or damage the Website.
- Scrape, copy or harvest content or data by automated means without our written permission.
- Use the Website in a way that infringes the rights of others or breaks any law.

## Intellectual property

The Website, including its text, graphics, logos, layout, software and compilation, is owned by or licensed to {{name}} and protected by intellectual property laws. Product names, brands and trademarks belonging to manufacturers remain theirs.

You may view and print pages for your own internal business purposes. You may not reproduce, modify, distribute or commercially exploit any content without our prior written consent.

## Information you submit

When you send us content such as messages, specifications or requirements, you give us permission to use it to respond to you and to provide our services. You confirm you have the right to share it. We handle personal information as described in our [Privacy Policy](/privacy-policy).

## Third-party links and content

The Website may link to third-party sites such as manufacturers or social platforms. These links are for convenience, and we do not control or endorse their content, accuracy or practices. Your use of those sites is at your own risk and subject to their terms.

## Disclaimers

The Website and its content are provided "as is" and "as available". To the fullest extent permitted by law, we disclaim all warranties, express or implied, including warranties of accuracy, merchantability, fitness for a particular purpose and non-infringement, and we do not guarantee that the Website will be uninterrupted, error-free or free from harmful components.

Nothing on the Website is professional, scientific, regulatory or medical advice. Product warranties apply only as stated in your order documents or by the manufacturer.

## Limitation of liability

To the fullest extent permitted by law, {{name}} will not be liable for:

- Indirect, incidental, special or consequential loss, including loss of profit, revenue, data, research results or business opportunity.
- Loss arising from your reliance on Website content, or from interruptions or unavailability of the Website.
- Loss caused by events beyond our reasonable control.

Our total liability arising out of your use of the Website is limited to the greater of the amount you paid us in the 12 months before the claim or a nominal amount. Liability for products supplied under an order is governed by the order documents. Nothing in these Terms limits liability that cannot be limited by law, including for death or personal injury caused by negligence, or for fraud.

## Indemnity

You agree to indemnify and hold harmless {{name}}, its directors, employees and partners from claims, losses and expenses (including reasonable legal fees) arising from your breach of these Terms, your misuse of the Website or products, or your violation of any law or third-party right.

## Suspension and termination

We may suspend or restrict your access to the Website at any time, without notice, if we believe you have breached these Terms or if needed for security or maintenance. Provisions that by nature should continue, such as intellectual property, disclaimers, limitation of liability and governing law, survive termination.

## Governing law and disputes

These Terms are governed by the laws of {{jurisdiction}}. The courts of that jurisdiction have exclusive jurisdiction over any dispute arising from them, except where mandatory law gives you the right to bring proceedings elsewhere. We encourage you to contact us first so we can try to resolve any issue informally.

## General

- **Changes:** we may update these Terms at any time by posting the revised version with a new "Last updated" date. Continued use means you accept the changes.
- **Entire agreement:** these Terms, together with the policies linked above and any order documents, are the whole agreement about the Website.
- **Severability:** if a provision is found unenforceable, the rest remains in effect.
- **No waiver:** a delay in enforcing a right is not a waiver of it.
- **Assignment:** you may not transfer your rights under these Terms without our consent. We may assign ours to a successor to our business.

## Contact us

For questions about these Terms, please contact:

**{{legalName}}** ("{{name}}")
{{address}}
Email: [{{email}}](mailto:{{email}})
Phone: {{phone}}`,
};

/* ───────────────────────── COOKIE POLICY ───────────────────────── */

export const COOKIE_POLICY = {
    title: "Cookie Policy",
    content: `This policy explains what cookies and similar technologies are, which ones the {{name}} website uses, why we use them, and how you can control them.

## What are cookies?

Cookies are small text files that a website places on your device when you visit it. They let the site recognise your device, remember your actions and preferences, and work properly.

We also use similar technologies, such as local storage, session storage and pixels. In this policy, "cookies" refers to all of them.

## How we use cookies

{{name}} uses cookies to keep the website secure and functional, to remember your choices, and to understand how the site is used so we can improve it. The categories are:

- **Strictly necessary (no consent needed):** keep the website working, including page navigation, security, form submission and remembering your cookie choice.
- **Preferences (consent needed):** remember settings such as language or display choices so you do not have to set them again.
- **Analytics / performance (consent needed):** show us how visitors use the site, such as pages visited, time on page and errors, so we can improve it. Data is aggregated.
- **Marketing (consent needed):** measure the effectiveness of campaigns and show relevant content. We only use these if we run such campaigns.

The exact cookies in use can change as the site evolves. Where required by law, we set non-essential cookies only after you give consent.

## First-party and third-party cookies

- **First-party cookies** are set by smartlabtech.com directly.
- **Third-party cookies** are set by other organisations whose services we use, such as analytics or embedded-content providers. These providers have their own privacy policies and control how their cookies work.

## How long cookies last

- **Session cookies** are deleted when you close your browser.
- **Persistent cookies** stay on your device until they expire or you delete them. Most of ours last between a few days and 12 months.

## Managing your cookie choices

### On our website

When you first visit, you may be asked to accept or decline non-essential cookies. You can change your choice at any time by clearing your browser's stored site data for smartlabtech.com, after which you will be asked again.

### In your browser

Most browsers let you view, block or delete cookies in their settings. Blocking all cookies may stop parts of the website, such as forms, from working correctly. Look under "Privacy" or "Cookies" in the help section of Chrome, Firefox, Safari or Edge for instructions.

### Browser signals

Some browsers send "Do Not Track" or Global Privacy Control signals. Where the law requires us to honour them, we do.

## Cookies and personal data

Some cookies collect information that can identify you indirectly, such as an IP address or device identifier. We handle that information as described in our [Privacy Policy](/privacy-policy), including your rights to access, correct or delete it.

## Changes to this policy

We may update this Cookie Policy when we change the cookies we use or when the law changes. The "Last updated" date at the top shows the latest version. Where changes are significant, we will ask for your consent again if required.

## Contact us

If you have questions about how we use cookies, please contact:

**{{legalName}}** ("{{name}}")
{{address}}
Email: [{{email}}](mailto:{{email}})
Phone: {{phone}}`,
};